/**
 * `/flows/new` — pick a starting point, name it, create a DRAFT. Slice 69.
 *
 * The four starting points are the contract's `FLOW_TEMPLATES`: a welcome
 * series for new Reach leads, a birthday greeting (an `anniversary_in_next`
 * rule on any date column the person picks), win back lapsed customers, and
 * blank. The API builds the template's graph; this screen only chooses it.
 *
 * `?template=` and `?segment=` preselect — Reach → Leads' "Start a welcome
 * flow" and a Sense finding's "Draft a flow" both land here — but nothing is
 * created until the person presses Create, and nothing is ever launched from
 * here: creating makes a draft and opens the builder.
 */
import { flowTemplateIdSchema, type FlowTemplateId } from '@wend/shared';
import { PageHeader } from '@wend/ui';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { errorMessage } from '../../lib/api/errors';
import { createFlow } from '../../lib/api/flows';
import { listSegments } from '../../lib/api/segments';
import { PILLAR_IDENTITY } from '../../lib/pillars';
import { useAsyncData } from '../../lib/use-async-data';
import { NewFlowForm } from './NewFlowForm';

export function NewFlowPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const parsedTemplate = flowTemplateIdSchema.safeParse(params.get('template'));
  const initialTemplate: FlowTemplateId = parsedTemplate.success ? parsedTemplate.data : 'blank';
  const initialSegment = params.get('segment');
  const segments = useAsyncData((signal) => listSegments({ page: 1, pageSize: 100, sort: 'name', direction: 'asc' }, signal), []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (input: { name: string; template: FlowTemplateId; audienceSegmentId: string | null }) => {
    setSubmitting(true);
    setError(null);
    try {
      const flow = await createFlow({ name: input.name, template: input.template, audienceSegmentId: input.audienceSegmentId });
      navigate(`/flows/${flow.id}`);
    } catch (cause) {
      setError(errorMessage(cause));
      setSubmitting(false);
    }
  };

  return (
    <AppLayout title="New flow">
      <PageHeader eyebrow={PILLAR_IDENTITY.flow.label} icon={PILLAR_IDENTITY.flow.icon} lede="Pick a starting point. You will shape it on the canvas before anything launches." />
      {segments.status === 'error' && segments.data === null && (
        <ErrorState error={segments.error} title="Could not load your segments" onRetry={segments.reload} className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5" />
      )}
      {segments.loadingFirst && <p className="text-[0.9rem] text-muted">Loading…</p>}
      {segments.data !== null && (
        <NewFlowForm
          segments={segments.data.items}
          initialTemplate={initialTemplate}
          initialSegment={initialSegment}
          submitting={submitting}
          error={error}
          onCreate={(input) => void create(input)}
        />
      )}
    </AppLayout>
  );
}
